import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plan } from '../../types/plan';
import {
  BILLING_INTERVAL_LABELS,
} from '../../types/licensingEnums';
import { planService } from '../../services/planService';
import { planFeatureService } from '../../services/planFeatureService';
import { useToast } from '../../components/UI/ToastProvider';
import { ConfirmModal } from '../../components/UI/ConfirmModal';
import { Button } from '../../components/UI/Button';

type PlanWithFeatureCount = Plan & { featureCount?: number };

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { message?: string; error?: string; title?: string } } })?.response?.data;
  if (response?.error)
  {
    return response.error;
  }
  if (response?.message)
  {
    return response.message;
  }
  if (response?.title)
  {
    return response.title;
  }
  return fallback;
};

export const PlansPage: React.FC = () => {
  const [plans, setPlans] = useState<PlanWithFeatureCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; plan: Plan | null }>({ open: false, plan: null });
  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    void fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try
    {
      setLoading(true);
      const data = await planService.getAll();
      const plansWithCounts = await Promise.all(
        data.map(async (plan) => {
          try
          {
            const features = await planFeatureService.getByPlanId(plan.id);
            return { ...plan, featureCount: features.length };
          }
          catch
          {
            return { ...plan, featureCount: undefined };
          }
        })
      );
      setPlans(plansWithCounts);
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao carregar planos.'), 'error');
    }
    finally
    {
      setLoading(false);
    }
  };

  const handleNew = () => {
    navigate('/admin/plans/new');
  };

  const handleEdit = (plan: Plan) => {
    navigate(`/admin/plans/${plan.id}/edit`);
  };

  const handleDelete = (plan: Plan) => {
    setConfirmDelete({ open: true, plan });
  };

  const confirmDeletePlan = async () => {
    if (!confirmDelete.plan)
    {
      return;
    }

    try
    {
      await planService.delete(confirmDelete.plan.id);
      setPlans(plans.filter((p) => p.id !== confirmDelete.plan!.id));
      addToast('Plano excluído com sucesso.', 'success');
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao excluir plano.'), 'error');
    }
    finally
    {
      setConfirmDelete({ open: false, plan: null });
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planos</h1>
          <p className="text-sm text-gray-600 mt-1">Catálogo comercial de planos e pacotes de features.</p>
        </div>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={handleNew}
        >
          Novo Plano
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500">Carregando...</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Preço</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Aplicações</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Intervalo</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Ordem</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Features</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-4 text-gray-500">Nenhum plano cadastrado.</td>
                </tr>
              )}
              {plans.map((plan) => (
                <tr key={plan.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono text-sm">{plan.code}</td>
                  <td className="py-3 px-4 text-sm">{plan.name}</td>
                  <td className="py-3 px-4 text-sm">{formatPrice(plan.price)}</td>
                  <td className="py-3 px-4 text-sm">
                    {plan.moduleCodes && plan.moduleCodes.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {plan.moduleCodes.map((code) => (
                          <span
                            key={code}
                            className="inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase bg-purple-100 text-purple-700"
                          >
                            {code}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">Nenhuma</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {BILLING_INTERVAL_LABELS[plan.billingInterval] ?? plan.billingInterval}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{plan.sortOrder}</td>
                  <td className="py-3 px-4 text-sm">
                    {plan.featureCount !== undefined ? (
                      <span className="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700">
                        {plan.featureCount}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        plan.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {plan.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2 items-center">
                      <Button variant="secondary" size="sm" onClick={() => handleEdit(plan)} title="Editar Plano">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(plan)} title="Excluir Plano">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.open}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o plano "${confirmDelete.plan?.name}"? Esta ação não poderá ser desfeita.`}
        onConfirm={() => void confirmDeletePlan()}
        onCancel={() => setConfirmDelete({ open: false, plan: null })}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};
